import { apiService, buildODataQuery, odataString } from './api.service'
import {
  Template,
  TemplateInstance,
  TemplateCategory,
  TemplateVariable,
  VariableType,
  PaginatedResponse,
  PaginationRequest
} from '../types'

/** Office host a template targets (mirrors the API's `TemplateType` enum). */
export type TemplateHostType = 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'

/** Shape of a template as returned by `GET /v1/templates` (TemplateODataDto). */
export interface TemplateODataDto {
  id: string
  tenantId?: string
  name: string
  description?: string | null
  category?: string | null
  folderId?: string | null
  type: TemplateHostType | string
  content: string
  isActive: boolean
  thumbnailUrl?: string | null
  filePath?: string | null
  fileSize?: number | null
  originalFileName?: string | null
  currentVersionNumber: number
  variables?: Array<{
    name: string
    displayName: string
    type: string
    defaultValue?: string | null
    required: boolean
    description?: string | null
  }> | null
  tags?: string[] | null
  createdAt: string
  updatedAt: string
}

const VARIABLE_TYPES: VariableType[] = [
  'text', 'number', 'date', 'boolean', 'select', 'multiselect', 'image', 'file', 'array', 'object',
]

const toVariableType = (type: string | undefined): VariableType => {
  const normalised = (type ?? 'text').toLowerCase() as VariableType
  return VARIABLE_TYPES.includes(normalised) ? normalised : 'text'
}

const looksLikeHtml = (content: string): boolean => /<\/?[a-z][\s\S]*>/i.test(content)

/** Convert the API DTO into the richer `Template` model the add-in components use. */
export const mapTemplate = (dto: TemplateODataDto): Template => {
  const content = dto.content ?? ''
  const variables: TemplateVariable[] = (dto.variables ?? []).map(v => ({
    id: v.name,
    name: v.name,
    displayName: v.displayName || v.name,
    type: toVariableType(v.type),
    defaultValue: v.defaultValue ?? undefined,
    required: v.required,
    description: v.description ?? undefined,
  }))

  return {
    id: dto.id,
    name: dto.name,
    description: dto.description ?? undefined,
    // The API stores free-form category names ("Sales", "Human Resources"); keep them as-is.
    category: (dto.category || 'other') as TemplateCategory,
    content: {
      format: looksLikeHtml(content) ? 'html' : 'markdown',
      body: content,
    },
    variables,
    metadata: {
      language: 'en',
      hostType: dto.type,
      thumbnailUrl: dto.thumbnailUrl ?? undefined,
      originalFileName: dto.originalFileName ?? undefined,
    },
    tags: dto.tags ?? [],
    version: String(dto.currentVersionNumber ?? 1),
    isPublished: dto.isActive,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdBy: '',
    lastModifiedBy: '',
  }
}

export interface TemplateListFilters {
  /** Restrict to templates for one Office host, e.g. `'Word'`. */
  type?: TemplateHostType
  category?: TemplateCategory | string
  tags?: string[]
  /** Maps to `IsActive` on the API. */
  isPublished?: boolean
  folderId?: string
}

const buildTemplateFilter = (filters?: TemplateListFilters): string | undefined => {
  if (!filters) return undefined
  const clauses: string[] = []
  if (filters.type) clauses.push(`type eq ${odataString(filters.type)}`)
  if (filters.category) clauses.push(`category eq ${odataString(String(filters.category))}`)
  if (filters.isPublished !== undefined) clauses.push(`isActive eq ${filters.isPublished}`)
  if (filters.folderId) clauses.push(`folderId eq ${filters.folderId}`)
  return clauses.length ? clauses.join(' and ') : undefined
}

export class TemplateService {
  private readonly basePath = '/v1/templates'

  private async list(
    filters?: TemplateListFilters,
    pagination?: PaginationRequest,
    search?: string
  ): Promise<PaginatedResponse<Template>> {
    const params = buildODataQuery(
      pagination,
      buildTemplateFilter(filters),
      search ? { $search: search } : undefined
    )
    const page = await apiService.getPaginated<TemplateODataDto>(this.basePath, params, pagination)
    return { ...page, items: page.items.map(mapTemplate) }
  }

  async getTemplates(
    params?: PaginationRequest & TemplateListFilters
  ): Promise<PaginatedResponse<Template>> {
    const { type, category, tags, isPublished, folderId, ...pagination } = params ?? {}
    return this.list({ type, category, tags, isPublished, folderId }, pagination)
  }

  async getTemplate(id: string): Promise<Template> {
    const response = await apiService.get<TemplateODataDto>(`${this.basePath}/${id}`)
    return mapTemplate(response.data)
  }

  async createTemplate(template: Omit<Template, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'lastModifiedBy'>): Promise<Template> {
    const response = await apiService.post<TemplateODataDto>(this.basePath, template)
    return mapTemplate(response.data)
  }

  async updateTemplate(id: string, updates: Partial<Template>): Promise<Template> {
    const response = await apiService.put<TemplateODataDto>(`${this.basePath}/${id}`, updates)
    return mapTemplate(response.data)
  }

  async deleteTemplate(id: string): Promise<void> {
    await apiService.delete(`${this.basePath}/${id}`)
  }

  async duplicateTemplate(id: string, newName: string): Promise<Template> {
    const response = await apiService.post<TemplateODataDto>(`${this.basePath}/${id}/duplicate`, {
      name: newName,
    })
    return mapTemplate(response.data)
  }

  async publishTemplate(id: string): Promise<Template> {
    const response = await apiService.post<TemplateODataDto>(`${this.basePath}/${id}/publish`)
    return mapTemplate(response.data)
  }

  async unpublishTemplate(id: string): Promise<Template> {
    const response = await apiService.post<TemplateODataDto>(`${this.basePath}/${id}/unpublish`)
    return mapTemplate(response.data)
  }

  async searchTemplates(
    query: string,
    filters?: TemplateListFilters,
    pagination?: PaginationRequest
  ): Promise<PaginatedResponse<Template>> {
    return this.list(filters, pagination, query)
  }

  async getTemplatesByCategory(
    category: TemplateCategory,
    pagination?: PaginationRequest
  ): Promise<PaginatedResponse<Template>> {
    return this.list({ category }, pagination)
  }

  async getTemplatesByTag(
    tag: string,
    pagination?: PaginationRequest
  ): Promise<PaginatedResponse<Template>> {
    const page = await this.list(undefined, pagination)
    return { ...page, items: page.items.filter(t => t.tags.includes(tag)) }
  }

  async getMyTemplates(pagination?: PaginationRequest): Promise<PaginatedResponse<Template>> {
    return this.list(undefined, pagination)
  }

  async getSharedTemplates(pagination?: PaginationRequest): Promise<PaginatedResponse<Template>> {
    return this.list(undefined, pagination)
  }

  async shareTemplate(id: string, userIds: string[]): Promise<void> {
    await apiService.post(`${this.basePath}/${id}/share`, { userIds })
  }

  async unshareTemplate(id: string, userIds: string[]): Promise<void> {
    await apiService.post(`${this.basePath}/${id}/unshare`, { userIds })
  }

  // Template instance operations
  async createInstance(
    templateId: string,
    instance: Omit<TemplateInstance, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<TemplateInstance> {
    const response = await apiService.post<TemplateInstance>(
      `${this.basePath}/${templateId}/instances`,
      instance
    )
    return response.data
  }

  async getInstance(templateId: string, instanceId: string): Promise<TemplateInstance> {
    const response = await apiService.get<TemplateInstance>(
      `${this.basePath}/${templateId}/instances/${instanceId}`
    )
    return response.data
  }

  async updateInstance(
    templateId: string,
    instanceId: string,
    updates: Partial<TemplateInstance>
  ): Promise<TemplateInstance> {
    const response = await apiService.put<TemplateInstance>(
      `${this.basePath}/${templateId}/instances/${instanceId}`,
      updates
    )
    return response.data
  }

  async generateContent(
    templateId: string,
    instanceId: string
  ): Promise<{ content: string; tokensUsed?: number }> {
    const response = await apiService.post<{ content: string; tokensUsed?: number }>(
      `${this.basePath}/${templateId}/instances/${instanceId}/generate`
    )
    return response.data
  }

  async previewTemplate(
    templateId: string,
    variables: Record<string, any>
  ): Promise<{ preview: string }> {
    const response = await apiService.post<{ preview: string }>(
      `${this.basePath}/${templateId}/preview`,
      { variables }
    )
    return response.data
  }

  async validateVariables(
    templateId: string,
    variables: Record<string, any>
  ): Promise<{ valid: boolean; errors: Record<string, string[]> }> {
    const response = await apiService.post<{ valid: boolean; errors: Record<string, string[]> }>(
      `${this.basePath}/${templateId}/validate`,
      { variables }
    )
    return response.data
  }

  async exportTemplate(
    templateId: string,
    format: 'docx' | 'pdf' | 'html'
  ): Promise<Blob> {
    const response = await apiService.get(
      `${this.basePath}/${templateId}/export`,
      {
        params: { format },
        responseType: 'blob',
      }
    )
    return response.data as unknown as Blob
  }

  async importTemplate(file: File): Promise<Template> {
    const response = await apiService.upload<TemplateODataDto>(
      `${this.basePath}/import`,
      file
    )
    return mapTemplate(response.data)
  }
}

export const templateService = new TemplateService()
